function nonEmpty(v) {
  if (v === undefined || v === null) return false;
  return String(v).trim() !== '';
}

export function isValidEmail(v) {
  if (typeof v !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function isValidPhone(v) {
  if (typeof v !== 'string') return false;
  let cleaned = v.replace(/[\s-]/g, '');
  if (cleaned.startsWith('+91')) cleaned = cleaned.slice(3);
  else if (cleaned.startsWith('91') && cleaned.length === 12) cleaned = cleaned.slice(2);
  if (cleaned.startsWith('0') && cleaned.length === 11) cleaned = cleaned.slice(1);
  return /^\d{10}$/.test(cleaned);
}

export function isValidAadhar(v) {
  if (typeof v !== 'string') return false;
  return /^\d{12}$/.test(v.trim());
}

export function isValidIFSC(v) {
  if (typeof v !== 'string') return false;
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.trim().toUpperCase());
}

export function isValidPAN(v) {
  if (typeof v !== 'string') return false;
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v.trim().toUpperCase());
}

export function isValidGST(v) {
  if (v === undefined || v === null) return true;
  if (typeof v !== 'string') return false;
  const trimmed = v.trim();
  if (trimmed === '') return true;
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(trimmed.toUpperCase());
}

export function passwordsMatch(a, b) {
  if (a === undefined || a === null || a === '') return false;
  if (b === undefined || b === null || b === '') return false;
  return a === b;
}

export function isStrongEnoughPassword(v) {
  if (typeof v !== 'string') return false;
  return v.trim().length >= 6;
}

export function validateStep6(values) {
  const v = values || {};
  const errors = {};

  if (!nonEmpty(v.roomTypeName)) {
    errors.roomTypeName = 'Room type name is required';
  }
  const catId = Number(v.roomTypeCategoryId);
  if (!Number.isInteger(catId) || catId <= 0) {
    errors.roomTypeName = 'Pick a room type from the search list';
  }

  const rcNum = Number(v.roomCount);
  if (!Number.isInteger(rcNum) || rcNum < 1) {
    errors.roomCount = 'Enter a valid number of rooms';
  }

  const mo = Number(v.maxOccupancy);
  if (!Number.isInteger(mo) || mo < 1 || mo > 20) {
    errors.maxOccupancy = 'Enter occupancy between 1 and 20';
  }

  const rates = v.occupancyRates && typeof v.occupancyRates === 'object' ? v.occupancyRates : {};
  if (Number.isInteger(mo) && mo >= 1) {
    let prev = 0;
    for (let i = 1; i <= mo; i += 1) {
      const key = `${i}guest`;
      const n = Number(rates[key]);
      if (!Number.isFinite(n) || n <= 0) {
        errors[`rate_${i}`] = `Enter price for ${i} guest${i > 1 ? 's' : ''}`;
        if (!errors.occupancyRates) errors.occupancyRates = 'Enter all guest prices';
      } else if (prev > 0 && n < prev) {
        errors[`rate_${i}`] = `Must be ≥ ${i - 1}-guest price (₹${prev})`;
        errors[`rate_${i - 1}`] = errors[`rate_${i - 1}`] || `Must be ≤ ${i}-guest price (₹${n})`;
        if (!errors.occupancyRates) {
          errors.occupancyRates = 'Guest prices must increase or stay equal (1 ≤ 2 ≤ 3…)';
        }
      } else {
        prev = n;
      }
    }
  }

  if (!nonEmpty(v.rateStartDate)) {
    errors.rateStartDate = 'Start date is required';
  }
  if (!nonEmpty(v.rateEndDate)) {
    errors.rateEndDate = 'End date is required';
  }
  if (nonEmpty(v.rateStartDate) && nonEmpty(v.rateEndDate) && v.rateEndDate < v.rateStartDate) {
    errors.rateEndDate = 'End date must be on or after start date';
  }

  return errors;
}

/**
 * Validate every room type. Adds a duplicate-category error to later rooms that
 * reuse a category already picked for an earlier room.
 */
export function validateRooms(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const perRoom = list.map((room) => validateStep6(room));
  const seen = new Set();
  list.forEach((room, i) => {
    const id = String(room?.roomTypeCategoryId || '').trim();
    if (!id) return;
    if (seen.has(id)) perRoom[i].roomTypeName = 'This room type is already added';
    else seen.add(id);
  });
  const firstInvalidIndex = perRoom.findIndex((errors) => Object.keys(errors).length > 0);
  return {
    ok: list.length > 0 && firstInvalidIndex === -1,
    isEmpty: list.length === 0,
    perRoom,
    firstInvalidIndex,
  };
}

export function validateAllForSubmit(state) {
  const s = state || {};
  const stepErrors = [{}, {}, {}, {}, {}, {}, {}, {}];
  let step = 0;

  // Step 1: Account Setup
  if (!nonEmpty(s.fullName)) {
    stepErrors[0].fullName = 'Full name is required';
  }
  if (!isValidEmail(s.accountEmail)) {
    stepErrors[0].accountEmail = 'Valid email is required';
  }
  if (!isValidPhone(s.accountPhone)) {
    stepErrors[0].accountPhone = 'Valid phone number is required';
  }
  if (!isStrongEnoughPassword(s.password)) {
    stepErrors[0].password = 'Password must be at least 6 characters';
  }
  if (!passwordsMatch(s.password, s.confirmPassword)) {
    stepErrors[0].confirmPassword = 'Passwords do not match';
  }

  // Step 2: Property Type
  if (!nonEmpty(s.propertyName)) {
    stepErrors[1].propertyName = 'Property name is required';
  }
  if (!nonEmpty(s.selectedPropertyType)) {
    stepErrors[1].selectedPropertyType = 'Property type is required';
  }

  // Step 3: Location
  if (!nonEmpty(s.address)) {
    stepErrors[2].address = 'Address is required';
  }
  if (!nonEmpty(s.city)) {
    stepErrors[2].city = 'City is required';
  }
  if (!nonEmpty(s.state)) {
    stepErrors[2].state = 'State is required';
  }
  if (!nonEmpty(s.country)) {
    stepErrors[2].country = 'Country is required';
  }

  // Step 4: Description (property phone/email reuse the account contact)
  if (!nonEmpty(s.propertyDescription)) {
    stepErrors[3].propertyDescription = 'Property description is required';
  }

  // Step 5: Amenities & Policies
  if (!Array.isArray(s.selectedAmenities) || s.selectedAmenities.length === 0) {
    stepErrors[4].selectedAmenities = 'Select at least one amenity';
  }
  if (!Array.isArray(s.selectedHotelPolicies) || s.selectedHotelPolicies.length === 0) {
    stepErrors[4].selectedHotelPolicies = 'Select at least one policy';
  }
  if (!Array.isArray(s.selectedCancellationPolicies) || s.selectedCancellationPolicies.length !== 1) {
    stepErrors[4].selectedCancellationPolicies = 'Select exactly one cancellation policy';
  }

  // Step 6 is property images (in-memory files; not validated here)
  // Step 7: Room Setup (one entry per room type)
  const roomsResult = validateRooms(s.rooms);
  if (roomsResult.isEmpty) {
    stepErrors[6].rooms = 'Add at least one room type';
  } else if (!roomsResult.ok) {
    stepErrors[6] = Object.assign({}, ...roomsResult.perRoom);
  }

  // Step 8: KYC & Compliance
  if (!s.agreedToPolicy) {
    stepErrors[7].agreedToPolicy = 'You must agree to the policy to continue';
  }

  const errors = {};
  for (let i = 0; i < stepErrors.length; i++) {
    if (Object.keys(stepErrors[i]).length > 0) {
      if (step === 0) step = i + 1;
      Object.assign(errors, stepErrors[i]);
    }
  }
  const ok = Object.keys(errors).length === 0;
  return { ok, errors, step: ok ? 0 : step };
}
